import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play } from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import { api, type WorkflowScheduleRun } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import { FoldList, FoldRow } from "../../../components/FoldList.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import ExecutionStatusChip, { executionGist } from "../../../components/workflows/ExecutionStatusChip.tsx";
import { formatDate } from "../../../utils/format.ts";

const SKIP_REASONS: Record<string, string> = {
  previous_run_active: "skipped, the previous run was still working",
};

function gist(run: WorkflowScheduleRun): string {
  if (run.status === "skipped") return SKIP_REASONS[run.skip_reason ?? ""] ?? "skipped";
  return run.executions.length ? executionGist(run.executions) : "no step ran";
}

/** What the agent reported, when it is a text or structured agent's summary. */
function summaryOf(run: WorkflowScheduleRun): string | null {
  const agent = run.executions.find((e) => e.step_type === "agent");
  const summary = agent?.result.summary;
  return typeof summary === "string" && summary.trim() ? summary.trim() : null;
}

/** A saved schedule workflow's latest runs, newest first, and "Run now". */
export default function ScheduleRuns({
  workflowId,
  active,
  readOnly,
}: {
  workflowId: string;
  active: boolean;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const runsQuery = useQuery({
    queryKey: keys.workflowScheduleRun.list(workflowId),
    queryFn: () => api.getWorkflowScheduleRuns(workflowId),
    meta: { live: true },
  });
  const runNow = useMutation({
    mutationFn: () => api.runWorkflowNow(workflowId),
    onSuccess: (run) => {
      toast.success("Run started");
      setOpen(run.id);
      void queryClient.invalidateQueries({ queryKey: keys.workflowScheduleRun.list(workflowId) });
      void queryClient.invalidateQueries({ queryKey: keys.workflow.detail(workflowId) });
    },
  });
  const runs = runsQuery.data ?? [];

  return (
    <Stack spacing={1}>
      <SectionHeading
        title="Recent runs"
        count={runsQuery.data ? runs.length : undefined}
        action={
          !readOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Play />}
              disabled={!active}
              loading={runNow.isPending}
              onClick={() => runNow.mutate()}
            >
              Run now
            </Button>
          )
        }
      />
      {runsQuery.isLoading ? (
        <CenteredSpinner />
      ) : runs.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {active ? "No runs yet. The first comes at the next slot of the schedule." : "No runs yet. The workflow is paused."}
        </Typography>
      ) : (
        <FoldList>
          {runs.map((run) => {
            const summary = summaryOf(run);
            return (
              <FoldRow
                key={run.id}
                label={`${formatDate(run.scheduled_for, "datetime")}${run.kind === "manual" ? " · Run now" : ""}`}
                summary={gist(run)}
                expanded={open === run.id}
                onToggle={() => setOpen(open === run.id ? null : run.id)}
              >
                <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                  {run.status === "skipped" && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {gist(run)}.
                    </Typography>
                  )}
                  {run.executions.map((e) => (
                    <ExecutionStatusChip key={e.id} execution={e} />
                  ))}
                  {summary && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {summary}
                    </Typography>
                  )}
                </Stack>
              </FoldRow>
            );
          })}
        </FoldList>
      )}
    </Stack>
  );
}
